// The shared voice manager for Home Key: one map of held voices driven by
// ANY input path — the pointer piano, the typing keyboard, or a MIDI
// device — so they all sound the current patch and light up the same keys.
//
// useHomeKeyVoices(patch, recorder?) → { press, release, activeMidis, releaseAll }
//
// `recorder` is an optional { onNoteStart(midi, tMs), onNoteEnd(midi, tMs) }
// object (tMs is performance.now() at the moment of the event) so Echo/Try
// moments can capture { midi, at, dur } relative to their own record start.
// It may change between renders — the latest one is always used.

import { useCallback, useEffect, useRef, useState } from "react";
import { startNote, resumeAudio } from "../../lib/homeKeyAudio";

export default function useHomeKeyVoices(patch, recorder = null) {
  const voices = useRef(new Map()); // midi → { stop }
  const patchRef = useRef(patch);
  patchRef.current = patch;
  const recRef = useRef(recorder);
  recRef.current = recorder;
  const [activeMidis, setActiveMidis] = useState(() => new Set());

  const press = useCallback((midi) => {
    if (voices.current.has(midi)) return;
    resumeAudio(); // a press is always a user gesture — safe to resume here
    voices.current.set(midi, startNote(patchRef.current, midi));
    setActiveMidis((s) => {
      const n = new Set(s);
      n.add(midi);
      return n;
    });
    const rec = recRef.current;
    if (rec && rec.onNoteStart) rec.onNoteStart(midi, performance.now());
  }, []);

  const release = useCallback((midi) => {
    const voice = voices.current.get(midi);
    if (voice) {
      voice.stop();
      voices.current.delete(midi);
      const rec = recRef.current;
      if (rec && rec.onNoteEnd) rec.onNoteEnd(midi, performance.now());
    }
    setActiveMidis((s) => {
      if (!s.has(midi)) return s;
      const n = new Set(s);
      n.delete(midi);
      return n;
    });
  }, []);

  const releaseAll = useCallback(() => {
    const now = performance.now();
    voices.current.forEach((voice, midi) => {
      voice.stop();
      const rec = recRef.current;
      if (rec && rec.onNoteEnd) rec.onNoteEnd(midi, now);
    });
    voices.current.clear();
    setActiveMidis((s) => (s.size ? new Set() : s));
  }, []);

  // Silence anything still held on unmount (the Map identity never changes,
  // so capturing it at mount time is safe).
  useEffect(() => {
    const held = voices.current;
    return () => {
      held.forEach((voice) => voice.stop());
      held.clear();
    };
  }, []);

  return { press, release, activeMidis, releaseAll };
}
